import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {
  startDrawing,
  draw,
  stopDrawing,
  clearSignature,
} from "layouts/customer-add/utils/validation-utils";

export function normalizeSignatureSrc(signature) {
  if (!signature || typeof signature !== "string") {
    return "";
  }
  const trimmed = signature.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("data:")) {
    return trimmed;
  }
  return `data:image/png;base64,${trimmed}`;
}

function DebitOrderSignaturePad({
  value,
  onChange,
  readOnly = false,
  error = "",
  disabled = false,
  label = "Signature as used for operating on the account",
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.onerror = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      };
      img.src = normalizeSignatureSrc(value);
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [value, readOnly]);

  const handleMouseDown = (event) => {
    if (disabled || readOnly) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    setIsDrawing(true);
    const ctx = canvas.getContext("2d");
    startDrawing(event, canvas, ctx);
  };

  const handleMouseMove = (event) => {
    if (!isDrawing || disabled || readOnly) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    draw(event, canvas, ctx);
  };

  const handleMouseUp = () => {
    if (!isDrawing || disabled || readOnly) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    setIsDrawing(false);
    stopDrawing(canvas);
    onChange(canvas.toDataURL("image/png"));
  };

  const handleClear = () => {
    if (disabled || readOnly) {
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    clearSignature(canvas);
    onChange(null);
  };

  const imageSrc = normalizeSignatureSrc(value);

  if (readOnly) {
    return (
      <MDBox>
        <MDTypography variant="body2" color="text" mb={1}>
          {label}
        </MDTypography>
        {imageSrc ? (
          <Box
            component="img"
            src={imageSrc}
            alt="Debit order signature"
            sx={{
              display: "block",
              maxWidth: "100%",
              maxHeight: 200,
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: 1,
              bgcolor: "#fff",
            }}
          />
        ) : (
          <MDTypography variant="body2" color="text">
            No signature on file
          </MDTypography>
        )}
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDTypography variant="body2" color="text" mb={1}>
        {label}
      </MDTypography>
      <MDBox
        sx={{
          border: (theme) =>
            `1px solid ${error ? theme.palette.error.main : theme.palette.grey[300]}`,
          borderRadius: 1,
          position: "relative",
          height: 200,
          bgcolor: "#fff",
        }}
      >
        <canvas
          ref={canvasRef}
          width={600}
          height={200}
          style={{
            width: "100%",
            height: "100%",
            touchAction: "none",
            cursor: disabled ? "not-allowed" : isDrawing ? "crosshair" : "default",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseOut={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        />
        {!value && (
          <MDBox
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            <MDTypography variant="body2" color="text">
              Sign here
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
      {error && <FormHelperText error>{error}</FormHelperText>}
      <Button
        variant="outlined"
        size="small"
        onClick={handleClear}
        disabled={disabled}
        sx={{ mt: 1 }}
      >
        Clear signature
      </Button>
    </MDBox>
  );
}

DebitOrderSignaturePad.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string,
};

DebitOrderSignaturePad.defaultProps = {
  value: null,
  onChange: () => {},
  readOnly: false,
  error: "",
  disabled: false,
};

export default DebitOrderSignaturePad;
